/**
 * The marks this screen needs, drawn here rather than taken from the icon
 * component.
 *
 * That component rebuilds its svg with a viewBox and a width but no height, and
 * hands it to a wrapper span that has no layout of its own. A percentage width
 * inside an inline box is not something browsers agree on: Chrome on a laptop
 * resolved it, the WebKit engine every browser on an iPhone runs did not, and
 * the glyphs came out pressed into squares. The marks already drawn inline on
 * this screen, the chevron and the check and the thumb, never had the problem.
 *
 * Each of these carries its own width and height, so there is nothing left to
 * infer.
 */

const SIZE = 16;

interface IProps {
  /** A mark standing alone in a button carries more than one set beside a line
   * of text, and is drawn larger for it. */
  size?: number;
}

const getBase = (size: number = SIZE) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  // The weight the hand-drawn document icon in the indicator row already uses at
  // this size, and close to the fine line the Streamline set is drawn with. At 2
  // these read as a different, heavier family than everything around them.
  strokeWidth: 1,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  focusable: "false" as const,
  "aria-hidden": true,
});

export const PhoneIcon = (props: IProps) => (
  <svg {...getBase(props.size)}>
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

export const EmailIcon = (props: IProps) => (
  <svg {...getBase(props.size)}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

export const PdfIcon = (props: IProps) => (
  <svg {...getBase(props.size)}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="17" x2="16" y2="17" />
    <line x1="8" y1="13" x2="13" y2="13" />
  </svg>
);

export const ProfileIcon = (props: IProps) => (
  <svg {...getBase(props.size)}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const ClockIcon = (props: IProps) => (
  <svg {...getBase(props.size)}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

/** The mark the no-show button and label already use. */
export const ThumbDownIcon = (props: IProps) => (
  <svg {...getBase(props.size)}>
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
    <path d="M17 2h4a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-4" />
  </svg>
);

/** A clock turning back. */
export const HistoryIcon = (props: IProps) => (
  <svg {...getBase(props.size)}>
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <polyline points="3 3 3 8 8 8" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

export const NoteIcon = (props: IProps) => (
  <svg {...getBase(props.size)}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

export const HomeIcon = (props: IProps) => (
  <svg {...getBase(props.size)}>
    <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />
  </svg>
);
